import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import {
  SearchCategory,
  SearchParentCategory,
} from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { MenuItem, Box, Typography, Theme, SxProps, Select } from '@mui/material';

import { handleMenuItemClick } from './filterDropdown.util';
import { FilterParentRender } from './FilterParentRender';

type TopLevelMenuSelectProps = Readonly<{
  menuItems: SearchParentCategory['categories'];
  selectedItems: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  setSelectedParentKey: (selectedParentKey: SearchCategory | null) => void;
  iconName: AppIcons;
  name: string;
  hasSelectedItems: boolean;
}>;

export const TopLevelMenuSelect = ({
  menuItems,
  selectedItems,
  onSelect,
  setAnchorEl,
  setSelectedParentKey,
  iconName,
  name,
  hasSelectedItems,
}: TopLevelMenuSelectProps) => {
  // Get the keys of all categories in this dropdown
  const currentDropdownKeys: SearchCategory[] = menuItems.flatMap((item) => {
    const subItems = item.subCategories?.map((subItem) => subItem.key);

    return [item.key, ...(subItems || [])];
  });

  // Filter the selectedItems to only include this dropdown's items
  const selectedItemsInThisDropdown = selectedItems.filter((item) =>
    currentDropdownKeys.includes(item),
  );

  const selectedItemsFromOtherDropdowns = selectedItems.filter(
    (item) => !currentDropdownKeys.includes(item),
  );

  const isAnyItemSelected =
    hasSelectedItems ||
    menuItems.some(
      (item) =>
        selectedItems.includes(item.key) ||
        (item.subCategories &&
          item.subCategories.some((subItem) => selectedItems.includes(subItem.key))),
    );

  return (
    <Select
      multiple
      value={selectedItemsInThisDropdown || []}
      displayEmpty
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          sx: menuPaperPropsSx,
        },
      }}
      onChange={() => null}
      renderValue={() => (
        <FilterParentRender iconName={iconName} isAnyItemSelected={isAnyItemSelected} name={name} />
      )}
    >
      {menuItems.map((item) => {
        const isAnySubMenuItemSelected = item.subCategories?.some((subItem) =>
          selectedItems.includes(subItem.key),
        );

        return (
          <MenuItem
            key={item.key}
            value={item.key}
            disableRipple
            disableTouchRipple
            onClick={(event) =>
              handleMenuItemClick({
                clickedKey: item.key,
                event,
                menuItems,
                selectedItemsInThisDropdown,
                selectedItemsFromOtherDropdowns,
                onSelect,
                setAnchorEl,
                setSelectedParentKey,
              })
            }
          >
            <Box sx={menuItemContentStyle}>
              {!item.isParent && (
                <Box sx={menuItemIconStyle(selectedItemsInThisDropdown?.includes(item.key))}>
                  <AppIcon
                    iconName={selectedItemsInThisDropdown?.includes(item.key) ? 'Check' : 'Square'}
                    diameter="16px"
                    color={
                      selectedItemsInThisDropdown?.includes(item.key)
                        ? 'Colors/Foreground/fg-white'
                        : 'currentColor'
                    }
                  />
                </Box>
              )}

              <Typography
                variant="text-md-medium"
                sx={({ backrs }) => ({
                  color: ({ backrs }) => {
                    if (!item.isParent) {
                      return undefined;
                    }

                    return isAnySubMenuItemSelected
                      ? backrs.colors['Colors/Text/text-primary']
                      : backrs.colors['Colors/Text/text-placeholder'];
                  },
                  '&:hover': {
                    color: backrs.colors['Colors/Text/text-primary'],
                  },
                })}
              >
                {item.label}
              </Typography>
              {item.subCategories && (
                <Box sx={menuItemIconStyle(false)}>
                  <AppIcon
                    iconName="ChevronRight"
                    diameter="16px"
                    color={
                      isAnySubMenuItemSelected
                        ? 'Colors/Text/text-primary'
                        : 'Colors/Text/text-placeholder'
                    }
                  />
                </Box>
              )}
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

const menuItemContentStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const menuItemIconStyle =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const menuPaperPropsSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});
